import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { employeeAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Upload, Eye, Trash2, FileCheck2, FileText } from 'lucide-react';

export default function CancelledChequeUpload({ employeeId }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const loadMeta = () => {
    employeeAPI.getChequeMeta(employeeId)
      .then(res => setMeta(res.data))
      .catch(() => setMeta({ exists: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (employeeId) loadMeta();
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5 MB.');
      return;
    }
    setBusy(true);
    try {
      await employeeAPI.uploadCheque(employeeId, file);
      toast.success('Cancelled cheque uploaded');
      loadMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setBusy(false);
  };

  const handleView = async () => {
    try {
      const res = await employeeAPI.getChequeBlob(employeeId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Could not open the file');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove the uploaded cancelled cheque?')) return;
    setBusy(true);
    try {
      await employeeAPI.deleteCheque(employeeId);
      toast.success('Cancelled cheque removed');
      loadMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
    setBusy(false);
  };

  if (!employeeId) {
    return (
      <div className="space-y-2 pt-2 border-t">
        <Label>Cancelled Cheque</Label>
        <p className="text-xs text-muted-foreground">Save the employee first, then reopen to upload a cancelled cheque for bank verification.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 pt-2 border-t" data-testid="cancelled-cheque-section">
      <Label>Cancelled Cheque <span className="text-xs font-normal text-muted-foreground">(for bank account verification)</span></Label>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleFile} data-testid="cheque-file-input" />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />Loading…
        </div>
      ) : meta?.exists ? (
        <div className="flex items-center justify-between rounded-md border bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900 px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            {meta.content_type === 'application/pdf'
              ? <FileText className="h-5 w-5 text-emerald-600 shrink-0" />
              : <FileCheck2 className="h-5 w-5 text-emerald-600 shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid="cheque-filename">{meta.filename}</p>
              <p className="text-[11px] text-muted-foreground">
                Uploaded {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}{meta.uploaded_by ? ` by ${meta.uploaded_by}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button type="button" variant="ghost" size="sm" onClick={handleView} data-testid="cheque-view-btn">
              <Eye className="h-4 w-4 mr-1" />View
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="cheque-replace-btn">
              <Upload className="h-4 w-4 mr-1" />Replace
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={handleDelete} disabled={busy} data-testid="cheque-delete-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" className="w-full border-dashed" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="cheque-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload Cancelled Cheque (JPG, PNG or PDF · max 5 MB)
        </Button>
      )}
    </div>
  );
}
